import { useEffect, useRef } from "react"
import type { AiMessage } from "@/entities/ai/types"
import { SourceLinks } from "./SourceLinks"

interface Props {
  messages: AiMessage[]
  streamedContent: string
  isStreaming: boolean
  workspaceSlug: string
}

export function ChatMessages({ messages, streamedContent, isStreaming, workspaceSlug }: Props) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, streamedContent])

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.length === 0 && !isStreaming && (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-gray-400">Ask anything about your documentation</p>
        </div>
      )}

      {messages.map(msg => (
        <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
          <div
            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
              msg.role === "user"
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-900"
            }`}
          >
            <div className="whitespace-pre-wrap">{msg.content}</div>
            {msg.role === "assistant" && msg.sources?.length > 0 && (
              <SourceLinks sources={msg.sources} workspaceSlug={workspaceSlug} />
            )}
          </div>
        </div>
      ))}

      {isStreaming && streamedContent && (
        <div className="flex justify-start">
          <div className="max-w-[85%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900">
            <div className="whitespace-pre-wrap">{streamedContent}</div>
            <span className="inline-block w-1 h-4 bg-gray-400 animate-pulse ml-0.5" />
          </div>
        </div>
      )}

      {isStreaming && !streamedContent && (
        <div className="flex justify-start">
          <div className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500">
            Thinking...
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
